import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import colors from '../../theme/colors';
import { connect } from 'react-redux';
import { NotificationBannerType } from '../../reducers/notificationBanners';
import useNotificationBanner from '../../hooks/useNotificationBanner';
import { IAutoDismissNotificationBanner, IAutoDismissNotificationBannerProps } from './types/NotificationBanners';
import { RootState } from '../../store';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';

// Todo: Fix css to show multiple banners at a time

const styles = ({
  bannerType,
  topPosition,
  isVisible,
  isMobile,
}: {
  bannerType: NotificationBannerType;
  topPosition: number;
  isVisible: boolean;
  isMobile: boolean;
}) => {
  return css`
    position: fixed;
    top: ${topPosition}px;
    left: ${isMobile ? 'inherit' : '35%'};
    max-width: ${isMobile ? 'inherit' : '480px'};
    width: 100%;
    margin: 0 auto;
    z-index: ${isMobile ? '1001' : '1000'};
    color: ${colors.white};
    text-align: center;
    font-size: 15px;
    opacity: ${isVisible ? 1 : 0};
    transition: top 0.5s ease-in-out, opacity 0.5s ease-in-out;
    background: ${bannerType === 'success'
      ? colors.light.green.primary
      : bannerType === 'warning'
      ? colors.light.warning.primary
      : colors.light.warning.primary};
  `;
};

const NotificationBannerContainer = styled.div`
  ${styles};
`;

const NotificationBanner: React.FC<IAutoDismissNotificationBanner> = ({ notificationBanner }) => {
  const { isMobile } = useMediaBreakpoints();
  const { id, type, message, hideTimeout = 3000 } = notificationBanner;
  const conditionedTimeOut = process.env.REACT_APP_ENV === 'testqa' ? 7000 : hideTimeout;

  const noHeaderPages = ['/billing', '/payment', '/profile'];
  const isPageNoHeader = noHeaderPages.filter(item => document.location.pathname.indexOf(item) > -1).length > 0;
  const visibleTopPosition = isPageNoHeader ? 0 : isMobile ? 0 : 65;
  const invisibleTopPosition = -50;
  const [isVisible, setIsVisible] = useState(true);
  const [topPosition, setTopPosition] = useState(invisibleTopPosition);
  const { removeNotificationBanner } = useNotificationBanner();

  useEffect(() => {
    const setVisibleTimer = setTimeout(() => {
      setIsVisible(true);
      setTopPosition(visibleTopPosition);
    }, 300);

    const setInvisibleTimer = setTimeout(() => {
      setTopPosition(invisibleTopPosition);
    }, conditionedTimeOut + 1000);

    const removeBannerTimer = setTimeout(() => {
      setIsVisible(false);
      removeNotificationBanner(id);
    }, conditionedTimeOut + 2000);

    return () => {
      clearTimeout(setVisibleTimer);
      clearTimeout(setInvisibleTimer);
      clearTimeout(removeBannerTimer);
    };
  }, []);

  if (!isVisible) return null;

  return (
    <NotificationBannerContainer
      id={id}
      bannerType={type}
      topPosition={topPosition}
      isVisible={isVisible}
      isMobile={isMobile}
    >
      {typeof message === 'string' ? (
        <div
          style={{
            padding: 11,
          }}
        >
          {message}
        </div>
      ) : (
        message
      )}
    </NotificationBannerContainer>
  );
};

const NotificationBanners: React.FC<IAutoDismissNotificationBannerProps> = ({ notificationBanners }) => {
  return (
    <>
      {notificationBanners &&
        notificationBanners.map((notificationBanner, index) => (
          <NotificationBanner key={index} notificationBanner={notificationBanner} />
        ))}
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    notificationBanners: state.notificationBanners,
  };
};

export default connect(mapStateToProps, null)(NotificationBanners);
